import { fmtDateTime } from '@/lib/dates.js';
import { useState, useMemo, Fragment, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, CheckCircle2, ChevronDown, ChevronRight, Edit2, Info, Key, Loader2, Lock, Mail, Power, PowerOff, RotateCcw, ScrollText, Search, Send, ShieldCheck, ShieldOff, Trash2, Unlock, UserPlus, X } from 'lucide-react';
import {
  team, adminUsers, adminPasswordReset, type AdminUserRow,
} from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { humanRole, humanStatus } from '@/lib/humanise';
import { DEFAULT_SITE_MEMBER_ROLE, SITE_CHANGE_ROLES, SITE_MEMBER_ROLES, initialSiteRole } from '@/lib/siteRoles';
import { PageHeader } from '@/components/layout/PageHeader';
import { toast } from 'sonner';
import { ActionMenu, ActionItem } from '@/components/ui/ActionMenu';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';


/**
 * Site Team — full member management for the active site.
 *
 * Site-scoped twin of Admin > Users. site_admin can manage everyone at
 * their own site; platform admins manage anyone, scoped here to whichever
 * site is currently active in their session.
 *
 * What's shown:
 *   - Pending invitations card (existing flow — invites + resend)
 *   - Members table with avatars, filters, sort, row expand, action menu
 *
 * Action visibility is role-aware: site_admin can lock/unlock/remove-from-site
 * and change roles (team.change_role, 13 Oct 2026 — not to or from Site Admin,
 * not their own), but actions that need platform-tier auth (force-disable,
 * reset MFA, delete user) are hidden for site_admin — backend would 403
 * anyway, so we don't expose them.
 */

const ROLES = [
  'site_admin', 'principal_investigator', 'sub_investigator', 'coordinator',
  'site_study_designer', 'regulatory_specialist', 'site_pharma',
  'finance_billing', 'monitor', 'auditor',
];

/**
 * Roles an invitation or a role change can grant — shared with the BE (one
 * list for both endpoints). Includes Monitor (13 Oct 2026); monitors reach a
 * study only through Monitor Assignments.
 */
const INVITE_ROLES = SITE_MEMBER_ROLES;
const DEFAULT_INVITE_ROLE = DEFAULT_SITE_MEMBER_ROLE;

const STATUSES = ['active', 'invited', 'locked', 'disabled'] as const;
type StatusFilter = (typeof STATUSES)[number] | 'all';
type MfaFilter = 'all' | 'yes' | 'no';
type RoleFilter = 'all' | string;
type SortKey = 'name' | 'status' | 'last_login' | 'created';
type SortDir = 'asc' | 'desc';

const CAN_INVITE = new Set(['platform_admin', 'application_admin', 'site_admin']);
const PLATFORM_TIER = new Set(['platform_admin', 'application_admin']);

function initials(fullName: string, email: string): string {
  const name = (fullName || email.split('@')[0] || '').trim();
  if (!name) return '?';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return ((parts[0]![0] ?? '') + (parts[parts.length - 1]![0] ?? '')).toUpperCase();
}

const AVATAR_COLOURS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-violet-500',
  'bg-rose-500', 'bg-cyan-500', 'bg-fuchsia-500', 'bg-teal-500',
];
function avatarColour(seed: string): string {
  let n = 0;
  for (let i = 0; i < seed.length; i++) n += seed.charCodeAt(i);
  return AVATAR_COLOURS[n % AVATAR_COLOURS.length]!;
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const m = Math.floor(diffSec / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30); if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

export function TeamPage() {
  const { user } = useAuth();
  const teamPerms = useMyPermissions();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const siteId = user?.activeSiteId ?? '';
  const siteName = user?.siteNames?.[siteId] ?? 'this site';
  const callerIsPlatform = !!user && PLATFORM_TIER.has(user.role);
  // team.invite is a single requireOperation on the backend, so the grid is
  // authoritative; CAN_INVITE stays as the pre-load fallback.
  const canInvite = teamPerms.holdsOperation('team.invite', [...CAN_INVITE]);
  // Platform tier changes anyone's role; team.change_role holders (Site Admin
  // by default) change members' roles at this site under the invite rules.
  const canChangeRoles = callerIsPlatform || teamPerms.holdsOperation('team.change_role', [...CAN_INVITE]);
  const canChangeRoleOf = (u: AdminUserRow, currentRole: string | null): boolean =>
    callerIsPlatform ||
    (canChangeRoles && u.user_id !== user?.id && !!currentRole && currentRole !== 'site_admin');

  // Filters + sort + search
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [mfaFilter, setMfaFilter] = useState<MfaFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Modals / action state — outcome messages go via sonner toasts (not inline banners).
  const [inviting, setInviting] = useState(false);
  // Default role = lowest privilege per client rule: new users should not
  // get elevated privilege by default. Inviter picks explicitly.
  const [inviteDraft, setInviteDraft] = useState({ email: '', full_name: '', role: DEFAULT_INVITE_ROLE });
  const [roleEdit, setRoleEdit] = useState<{ user: AdminUserRow; role: string } | null>(null);
  const [deletingUser, setDeletingUser] = useState<{ user: AdminUserRow; confirm_email: string } | null>(null);

  const users = useQuery({ queryKey: ['admin-users'], queryFn: () => adminUsers.list() });
  const pendingInvites = useQuery({
    queryKey: ['team-invitations', siteId],
    queryFn: () => team.listPending(siteId),
    enabled: !!siteId && canInvite,
  });

  // Members at THIS site only
  const siteMembers = useMemo(() => {
    if (!users.data || !siteId) return [];
    return users.data.rows.filter((u) => u.sites.some((s) => s.site_id === siteId));
  }, [users.data, siteId]);

  const usersById = useMemo(() => {
    const m = new Map<string, { full_name: string; email: string }>();
    users.data?.rows.forEach((u) => m.set(u.user_id, { full_name: u.full_name, email: u.email }));
    return m;
  }, [users.data]);

  const stats = useMemo(() => ({
    total: siteMembers.length,
    active: siteMembers.filter((u) => !u.locked_at && u.status === 'active').length,
    locked: siteMembers.filter((u) => !!u.locked_at).length,
    disabled: siteMembers.filter((u) => u.status === 'disabled').length,
    mfaEnrolled: siteMembers.filter((u) => u.mfa_enrolled).length,
  }), [siteMembers]);

  // Helper: extract a user's role at THIS site (separate from platform_role)
  const roleAtSite = (u: AdminUserRow): string | null =>
    u.sites.find((s) => s.site_id === siteId)?.role ?? null;

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const rows = siteMembers.filter((u) => {
      if (q && !u.email.toLowerCase().includes(q) && !u.full_name.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'all') {
        const effective = u.locked_at ? 'locked' : u.status;
        if (effective !== statusFilter) return false;
      }
      if (mfaFilter === 'yes' && !u.mfa_enrolled) return false;
      if (mfaFilter === 'no' && u.mfa_enrolled) return false;
      if (roleFilter !== 'all' && roleAtSite(u) !== roleFilter) return false;
      return true;
    });
    const cmp = (a: AdminUserRow, b: AdminUserRow) => {
      let v = 0;
      if (sortKey === 'name') v = (a.full_name || a.email).localeCompare(b.full_name || b.email);
      else if (sortKey === 'status') v = (a.status || '').localeCompare(b.status || '');
      else if (sortKey === 'last_login') {
        v = (a.last_login_at ? new Date(a.last_login_at).getTime() : 0)
          - (b.last_login_at ? new Date(b.last_login_at).getTime() : 0);
      } else if (sortKey === 'created') {
        v = (a.created_at ? new Date(a.created_at).getTime() : 0)
          - (b.created_at ? new Date(b.created_at).getTime() : 0);
      }
      return sortDir === 'asc' ? v : -v;
    };
    return [...rows].sort(cmp);
  }, [siteMembers, search, statusFilter, roleFilter, mfaFilter, sortKey, sortDir]);

  const onError = (e: unknown) =>
    toast.error(errorText(e, 'Action failed'));
  const onSuccess = (msg: string) => {
    toast.success(msg);
    qc.invalidateQueries({ queryKey: ['admin-users'] });
  };

  const inviteMut = useMutation({
    mutationFn: () => team.invite(siteId, {
      email: inviteDraft.email.trim(),
      full_name: inviteDraft.full_name.trim() || undefined,
      role: inviteDraft.role,
    }),
    onSuccess: () => {
      setInviting(false); setInviteDraft({ email: '', full_name: '', role: DEFAULT_INVITE_ROLE });
      onSuccess('Invitation sent.');
      qc.invalidateQueries({ queryKey: ['team-invitations', siteId] });
    },
    onError,
  });
  const resendMut = useMutation({
    mutationFn: (invite_id: string) => team.resend(siteId, invite_id),
    onSuccess: () => {
      onSuccess('Invitation resent.');
      qc.invalidateQueries({ queryKey: ['team-invitations', siteId] });
    },
    onError,
  });
  const lockMut = useMutation({
    mutationFn: (id: string) => adminUsers.lock(id),
    onSuccess: () => onSuccess('User locked.'), onError
  });
  const unlockMut = useMutation({
    mutationFn: (id: string) => adminUsers.unlock(id),
    onSuccess: () => onSuccess('User unlocked.'), onError
  });
  const enableMut = useMutation({
    mutationFn: (id: string) => adminUsers.enable(id),
    onSuccess: () => onSuccess('Account enabled.'), onError
  });
  const disableMut = useMutation({
    mutationFn: (id: string) => adminUsers.disable(id),
    onSuccess: () => onSuccess('Account disabled.'), onError
  });
  const resetMfaMut = useMutation({
    mutationFn: (id: string) => adminUsers.resetMfa(id),
    onSuccess: () => onSuccess('MFA reset — user will re-enrol on next login.'), onError
  });
  const resetPwMut = useMutation({
    mutationFn: (email: string) => adminPasswordReset.send(email),
    onSuccess: () => onSuccess('Password-reset email sent.'), onError
  });
  const removeFromSiteMut = useMutation({
    mutationFn: (userId: string) => adminUsers.removeSite(userId, siteId),
    onSuccess: () => onSuccess('User removed from site.'), onError,
  });
  const removeMut = useMutation({
    mutationFn: () => adminUsers.remove(deletingUser!.user.user_id, deletingUser!.confirm_email),
    onSuccess: () => { setDeletingUser(null); onSuccess('User deleted.'); }, onError,
  });
  const changeRoleMut = useMutation({
    mutationFn: () => adminUsers.changeRole(roleEdit!.user.user_id, siteId, roleEdit!.role),
    onSuccess: () => { setRoleEdit(null); onSuccess('Role updated.'); }, onError,
  });
  // Invitable roles plus Monitor — Change role is how a Site User becomes a
  // reviewer for Monitor Assignments. Site Admin is the platform tier's to grant.
  const roleChoices = callerIsPlatform ? SITE_CHANGE_ROLES : SITE_CHANGE_ROLES.filter((r) => r !== 'site_admin');
  const openRoleEdit = (u: AdminUserRow, currentRole: string | null) =>
    setRoleEdit({ user: u, role: initialSiteRole(currentRole, roleChoices) });

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir(k === 'last_login' || k === 'created' ? 'desc' : 'asc'); }
  }
  function sortIndicator(k: SortKey) {
    if (sortKey !== k) return null;
    return <span className="text-[10px]">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  }
  if (!canInvite) {
    return (
      <div className="card p-8 text-center text-ink-muted">
        <UserPlus className="mx-auto mb-3 h-10 w-10 opacity-60" />
        <h2 className="mb-1 text-lg font-semibold text-ink-primary">Team management</h2>
        <p className="text-sm">
          Only site admins can manage team members. Ask your site admin to invite you, or contact
          a platform admin.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Site Team"
        subtitle={`Team members at ${siteName}. Invitations expire after 48 hours.`}
        actions={
          <>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-muted" />
              <input type="text" className="input pl-7" placeholder="Search email or name"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <button type="button" className="btn-primary" onClick={() => setInviting(true)}>
              <UserPlus className="h-4 w-4" /> Invite member
            </button>
          </>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="card p-3"><div className="eyebrow mb-0.5">Total</div><div className="text-xl font-bold">{stats.total}</div></div>
        <div className="card p-3"><div className="eyebrow mb-0.5">Active</div><div className="text-xl font-bold text-success-700">{stats.active}</div></div>
        <div className="card p-3"><div className="eyebrow mb-0.5">Locked</div><div className="text-xl font-bold text-danger-700">{stats.locked}</div></div>
        <div className="card p-3"><div className="eyebrow mb-0.5">Disabled</div><div className="text-xl font-bold text-ink-muted">{stats.disabled}</div></div>
        <div className="card p-3"><div className="eyebrow mb-0.5">MFA Enrolled</div><div className="text-xl font-bold">{stats.mfaEnrolled}</div></div>
      </div>

      {/* Filters */}
      <div className="card p-3">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="label text-xs">Status</label>
            <select className="input" value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
              <option value="all">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{humanStatus(s)}</option>)}
            </select>
          </div>
          <div>
            <label className="label text-xs">Role at this site</label>
            <select className="input" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">All roles</option>
              {ROLES.map((r) => <option key={r} value={r}>{humanRole(r)}</option>)}
            </select>
          </div>
          <div>
            <label className="label text-xs">MFA enrolled</label>
            <select className="input" value={mfaFilter} onChange={(e) => setMfaFilter(e.target.value as MfaFilter)}>
              <option value="all">Any</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>
      </div>

      {/* Pending invitations */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-surface-border bg-surface-muted px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
            <Mail className="h-4 w-4" /> Pending invitations ({pendingInvites.data?.rows.length ?? 0})
          </div>
        </div>
        {pendingInvites.isLoading ? (
          <div className="flex items-center justify-center gap-2 p-4 text-xs text-ink-muted">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading invitations…
          </div>
        ) : !pendingInvites.data || pendingInvites.data.rows.length === 0 ? (
          <div className="p-6 text-center text-xs text-ink-muted">
            No pending invitations. Use <strong>Invite member</strong> to send a setup link.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Role on accept</th>
                <th className="px-3 py-2">Expires</th>
                <th className="px-3 py-2">Delivery</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingInvites.data.rows.map((inv) => (
                <tr key={inv.invite_id} className="border-b border-surface-border last:border-0">
                  <td className="px-3 py-2">{inv.email}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className="badge badge-info">{humanRole(inv.role)}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-muted">{fmtDateTime(inv.expires_at_iso)}</td>
                  <td className="px-3 py-2 text-xs">
                    {inv.email_status === 'failed' ? (
                      <span
                        className="badge badge-danger inline-flex items-center gap-1"
                        title={inv.email_error ?? 'The invite email could not be sent'}
                      >
                        <AlertTriangle className="h-3 w-3" /> Delivery failed
                      </span>
                    ) : (
                      <span className="badge badge-success">Sent</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" className="btn-ghost text-xs"
                      onClick={() => resendMut.mutate(inv.invite_id)} disabled={resendMut.isPending}>
                      <RotateCcw className="h-3 w-3" /> Resend
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Members */}
      <div className="card overflow-hidden">
        {users.isLoading && (
          <div className="flex items-center justify-center gap-2 p-8 text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading members…
          </div>
        )}
        {users.isError && (
          <div className={`flex items-start gap-2 p-4 text-sm ${errorToneClass(users.error)}`}>
            {isPermissionError(users.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>{errorText(users.error, 'Failed to load members')}</span>
          </div>
        )}
        {users.data && filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-ink-muted">
            <UserPlus className="mx-auto mb-2 h-10 w-10 opacity-50" />
            {search || statusFilter !== 'all' || roleFilter !== 'all' || mfaFilter !== 'all'
              ? 'No members match the current filters.'
              : 'No members yet.'}
          </div>
        )}
        {users.data && filtered.length > 0 && (
          <table className="min-w-full text-sm">
            <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-3 py-2 w-6"></th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('name')}>
                  <span className="inline-flex items-center gap-1">Member {sortIndicator('name')}</span>
                </th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('status')}>
                  <span className="inline-flex items-center gap-1">Status {sortIndicator('status')}</span>
                </th>
                <th className="px-3 py-2">Role at site</th>
                <th className="px-3 py-2">MFA</th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('last_login')}>
                  <span className="inline-flex items-center gap-1">Last login {sortIndicator('last_login')}</span>
                </th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('created')}>
                  <span className="inline-flex items-center gap-1">Created {sortIndicator('created')}</span>
                </th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isExpanded = expanded.has(u.user_id);
                const verified = !!u.email_verified_at || (!u.locked_at && !!u.last_login_at);
                const myRole = roleAtSite(u);
                const creator = u.created_by_user_id ? usersById.get(u.created_by_user_id) : null;
                const isSelf = u.user_id === user?.id;
                return (
                  <Fragment key={u.user_id}>
                    <tr className="border-b border-surface-border last:border-0 align-top hover:bg-surface-muted/30">
                      <td className="px-3 py-2">
                        <button type="button" onClick={() => toggleExpand(u.user_id)}
                          className="text-ink-muted hover:text-ink-primary">
                          {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2.5">
                          <div className={`h-8 w-8 shrink-0 rounded-full grid place-items-center text-white text-[10px] font-bold ${avatarColour(u.user_id)}`}>
                            {initials(u.full_name, u.email)}
                          </div>
                          <div>
                            <div className="font-medium flex items-center gap-1.5">
                              {u.full_name || u.email.split('@')[0]}
                              {verified && <span title="Email verified"><CheckCircle2 className="h-3 w-3 text-emerald-600" /></span>}
                            </div>
                            <div className="text-xs text-ink-muted">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {u.locked_at
                          ? <span className="badge badge-danger">Locked</span>
                          : <span className={u.status === 'active' ? 'badge badge-success' : u.status === 'disabled' ? 'badge badge-warn' : 'badge badge-info'}>{humanStatus(u.status)}</span>}
                        {u.lock_reason && <div className="mt-1 text-[10px] text-ink-muted">{u.lock_reason}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <span className="badge badge-info text-xs">{myRole ? humanRole(myRole) : 'No role'}</span>
                        {canChangeRoleOf(u, myRole) && (
                          <button type="button" className="ml-1 text-ink-muted hover:text-ink-primary"
                            onClick={() => openRoleEdit(u, myRole)}
                            aria-label="Change role" title="Change role">
                            <Edit2 className="h-3 w-3" />
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {u.mfa_enrolled ? <span className="text-success-700">Yes</span> : <span className="text-ink-muted">No</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-muted" title={u.last_login_at ?? ''}>{relativeTime(u.last_login_at)}</td>
                      <td className="px-3 py-2 text-xs text-ink-muted" title={u.created_at ?? ''}>{relativeTime(u.created_at)}</td>
                      <td className="px-3 py-2 text-right">
                        <ActionMenu label={`Actions for ${u.email}`}>
                          {u.locked_at ? (
                            <ActionItem icon={Unlock} onClick={() => unlockMut.mutate(u.user_id)}>
                              Unlock
                            </ActionItem>
                          ) : (
                            <ActionItem icon={Lock} onClick={() => lockMut.mutate(u.user_id)}>
                              Lock
                            </ActionItem>
                          )}
                          {callerIsPlatform && (u.status === 'disabled' ? (
                            <ActionItem icon={Power} onClick={() => enableMut.mutate(u.user_id)}>
                              Enable account
                            </ActionItem>
                          ) : (
                            <ActionItem
                              icon={PowerOff}
                              disabled={isSelf}
                              onClick={() => {
                                if (window.confirm(`Disable ${u.email}?\nThey'll no longer be able to log in.`)) {
                                  disableMut.mutate(u.user_id);
                                }
                              }}
                            >
                              Disable account
                            </ActionItem>
                          ))}
                          {canChangeRoleOf(u, myRole) && (
                            <ActionItem icon={Edit2} onClick={() => openRoleEdit(u, myRole)}>
                              Change role
                            </ActionItem>
                          )}
                          <ActionItem icon={Key} onClick={() => resetPwMut.mutate(u.email)}>
                            Send password reset
                          </ActionItem>
                          {callerIsPlatform && (
                            <ActionItem
                              icon={u.mfa_enrolled ? ShieldOff : ShieldCheck}
                              onClick={() => {
                                if (window.confirm(`Reset MFA for ${u.email}?`)) {
                                  resetMfaMut.mutate(u.user_id);
                                }
                              }}
                            >
                              Reset MFA
                            </ActionItem>
                          )}
                          <ActionItem
                            icon={ScrollText}
                            onClick={() => navigate(`/admin/audit?user_id=${u.user_id}`)}
                          >
                            View audit
                          </ActionItem>
                          <ActionItem
                            icon={Trash2}
                            danger
                            disabled={isSelf}
                            onClick={() => {
                              if (window.confirm(`Remove ${u.email} from ${siteName}?`)) {
                                removeFromSiteMut.mutate(u.user_id);
                              }
                            }}
                          >
                            Remove from site
                          </ActionItem>
                          {callerIsPlatform && (
                            <ActionItem
                              icon={Trash2}
                              danger
                              disabled={isSelf}
                              onClick={() => setDeletingUser({ user: u, confirm_email: '' })}
                            >
                              Delete user…
                            </ActionItem>
                          )}
                        </ActionMenu>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-surface-border bg-surface-muted/50">
                        <td></td>
                        <td colSpan={7} className="px-3 py-3 text-xs space-y-2">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <div className="font-semibold text-ink-primary mb-1">Account</div>
                              <div>User ID: <span className="font-mono">{u.user_id}</span></div>
                              <div>Created: {u.created_at ? fmtDateTime(u.created_at) : '—'}</div>
                              <div>Created by: {creator ? `${creator.full_name || creator.email}` : (u.created_by_user_id ? <span className="font-mono">{u.created_by_user_id.slice(0, 8)}</span> : '—')}</div>
                              <div>Last login: {u.last_login_at ? fmtDateTime(u.last_login_at) : '—'}</div>
                              <div>Email verified: {u.email_verified_at ? fmtDateTime(u.email_verified_at) : (verified ? 'Yes (active login)' : 'No')}</div>
                              <div>MFA enrolled: {u.mfa_enrolled ? 'Yes' : 'No'}</div>
                            </div>
                            <div>
                              <div className="font-semibold text-ink-primary mb-1">Roles</div>
                              <div>This site: <strong>{myRole ? humanRole(myRole) : '—'}</strong></div>
                              {u.platform_role && (
                                <div>Platform: <strong>{humanRole(u.platform_role)}</strong></div>
                              )}
                              {u.sites.length > 1 && (
                                <div className="mt-2">
                                  <div className="font-semibold text-ink-primary mb-1">Other sites</div>
                                  <ul className="ml-4 list-disc">
                                    {u.sites.filter((s) => s.site_id !== siteId).map((s) => (
                                      <li key={s.site_id} className="font-mono">
                                        {s.site_id.slice(0, 8)} — {s.role ? humanRole(s.role) : 'No role'}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {u.locked_at && (
                                <div className="mt-2">
                                  <div className="font-semibold text-ink-primary mb-1">Lock metadata</div>
                                  <div>Locked at: {fmtDateTime(u.locked_at)}</div>
                                  <div>Reason: <span className="font-mono">{u.lock_reason ?? '—'}</span></div>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Invite modal */}
      {inviting && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form onSubmit={(e: FormEvent) => { e.preventDefault(); if (inviteDraft.email.trim()) inviteMut.mutate(); }}
            className="card w-full max-w-md space-y-4 p-6">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold">Invite to {siteName}</h2>
              <button type="button" onClick={() => setInviting(false)}><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-ink-muted">
              Sends a setup-link email. Pick the role the invitee joins as —
              defaults to <strong>Site User</strong> to keep new users at the
              lowest privilege.
            </p>
            <div>
              <label className="label text-xs">Work email *</label>
              <input type="email" required className="input" placeholder="colleague@organisation.com"
                value={inviteDraft.email}
                onChange={(e) => setInviteDraft({ ...inviteDraft, email: e.target.value })} />
            </div>
            <div>
              <label className="label text-xs">Role *</label>
              <select
                className="input"
                value={inviteDraft.role}
                onChange={(e) => setInviteDraft({ ...inviteDraft, role: e.target.value })}
              >
                {/* Site admin can't grant site_admin (server enforces the same). */}
                {(callerIsPlatform ? INVITE_ROLES : INVITE_ROLES.filter((r) => r !== 'site_admin')).map((r) => (
                  <option key={r} value={r}>{humanRole(r)}</option>
                ))}
              </select>
              <p className="mt-1 text-[10px] text-ink-muted">
                Default is Site User — no study access until they're added to a study. Escalate only
                when the person actually needs that access. A Monitor or Auditor gets study access
                only through Monitor Assignments.
              </p>
            </div>
            <div>
              <label className="label text-xs">Full name (optional)</label>
              <input type="text" className="input" placeholder="Jane Smith"
                value={inviteDraft.full_name}
                onChange={(e) => setInviteDraft({ ...inviteDraft, full_name: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setInviting(false)}>Cancel</button>
              <button type="submit" className="btn-primary"
                disabled={inviteMut.isPending || !inviteDraft.email.trim()}>
                {inviteMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send invite
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Change role modal (platform tier, and team.change_role holders) */}
      {roleEdit && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form onSubmit={(e) => { e.preventDefault(); changeRoleMut.mutate(); }}
            className="card w-full max-w-md space-y-4 p-6">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold">Change role at {siteName}</h2>
              <button type="button" onClick={() => setRoleEdit(null)}><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-ink-muted">
              Changing role for <strong className="text-ink-primary">{roleEdit.user.email}</strong>.
              The member will need to log out and log back in for the new role to take effect.
            </p>
            <div>
              <label className="label text-xs">New role</label>
              <select className="input" value={roleEdit.role}
                onChange={(e) => setRoleEdit({ ...roleEdit, role: e.target.value })}>
                {roleChoices.map((r) => <option key={r} value={r}>{humanRole(r)}</option>)}
              </select>
              {!callerIsPlatform && (
                <p className="mt-1 text-[10px] text-ink-muted">
                  Site Admin can only be granted or removed by a platform administrator.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setRoleEdit(null)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={changeRoleMut.isPending}>
                {changeRoleMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Save
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete user modal (platform-tier callers only) */}
      {deletingUser && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form onSubmit={(e) => {
            e.preventDefault();
            if (deletingUser.confirm_email === deletingUser.user.email) removeMut.mutate();
          }} className="card w-full max-w-md space-y-4 p-6">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold text-danger-700">Delete user</h2>
              <button type="button" onClick={() => setDeletingUser(null)}><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-ink-muted">
              This permanently deletes <strong>{deletingUser.user.email}</strong> and cascades all
              memberships, delegations, MFA credentials, and access logs. Audit retained.
              Type the email to confirm.
            </p>
            <input type="text" required className="input"
              placeholder={deletingUser.user.email}
              value={deletingUser.confirm_email}
              onChange={(e) => setDeletingUser({ ...deletingUser, confirm_email: e.target.value })} />
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setDeletingUser(null)}>Cancel</button>
              <button type="submit" className="btn-danger"
                disabled={removeMut.isPending || deletingUser.confirm_email !== deletingUser.user.email}>
                {removeMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
